import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AgentLayout } from "@/components/AgentLayout";
import { TierBadge } from "@/components/TierBadge";
import { useAuth } from "@/contexts/AuthContext";
import { CircleNotch, Crown, Trophy, Medal, Suitcase } from "@phosphor-icons/react";

function formatMoney(n) {
  if (n == null || isNaN(n)) return "$0";
  return "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

const RANK_ICON = {
  1: { Icon: Crown, color: "text-[#D4AF37]", ring: "border-[#D4AF37]/60 bg-[#D4AF37]/10" },
  2: { Icon: Trophy, color: "text-slate-200", ring: "border-slate-300/40 bg-slate-300/10" },
  3: { Icon: Medal, color: "text-amber-700/90", ring: "border-amber-700/40 bg-amber-700/10" },
};

export default function Leaderboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    (async () => {
      setData(null);
      try {
        const { data } = await api.get(`/leaderboard?year=${year}`);
        setData(data);
      } catch {
        setData({ error: true });
      }
    })();
  }, [year]);

  const rows = data?.rows || [];
  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <AgentLayout>
      <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4" data-testid="leaderboard-header">
        <div>
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3 font-sans">Recognition</p>
          <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-white mb-3">
            Top <span className="italic text-[#D4AF37]">Agents</span>
          </h1>
          <p className="text-white/60 text-sm max-w-xl font-sans">
            Celebrating the curators who deliver unforgettable journeys. Ranked by annual booked revenue.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Year</label>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            data-testid="leaderboard-year-select"
            className="h-10 bg-ink-surface border border-white/10 text-white text-sm px-3 rounded-md focus:border-[#D4AF37] focus:outline-none font-sans"
          >
            {[0, 1, 2].map((offset) => {
              const y = new Date().getFullYear() - offset;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
        </div>
      </div>

      {!data ? (
        <div className="py-20 flex justify-center"><CircleNotch size={28} className="text-[#D4AF37] animate-spin" /></div>
      ) : data.error ? (
        <div className="rounded-lg border border-red-400/30 bg-red-500/5 p-6 text-red-300 text-sm font-sans">
          Could not load leaderboard.
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-ink-surface p-10 text-center">
          <Suitcase size={36} className="mx-auto text-white/30 mb-3" weight="duotone" />
          <p className="font-serif text-2xl text-white mb-1">No booked sales yet in {year}</p>
          <p className="text-white/50 text-sm font-sans">Rankings appear once itineraries are approved &amp; booked.</p>
        </div>
      ) : (
        <>
          {/* Podium — top 3 */}
          {top3.some((r) => r.annual_revenue > 0) && (
            <section className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8" data-testid="leaderboard-podium">
              {top3.map((r) => {
                const meta = RANK_ICON[r.rank];
                const Icon = meta?.Icon || Medal;
                const isMe = r.agent_id === user?.id;
                return (
                  <div
                    key={r.agent_id}
                    className={`relative rounded-lg border ${meta?.ring || "border-white/10 bg-ink-surface"} p-6 transition-all`}
                    data-testid={`podium-rank-${r.rank}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center border ${meta?.ring}`}>
                        <Icon size={22} className={meta?.color} weight="duotone" />
                      </div>
                      <span className={`text-[10px] uppercase tracking-[0.3em] font-sans ${meta?.color}`}>
                        Rank #{r.rank}
                      </span>
                    </div>
                    <p className="font-serif text-2xl text-white mb-1 truncate">
                      {r.name} {isMe && <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] ml-1">You</span>}
                    </p>
                    <p className="text-xs text-white/40 font-sans truncate mb-5">{r.email}</p>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1 font-sans">Annual sales</p>
                        <p className="font-serif text-xl text-[#D4AF37]">{formatMoney(r.annual_revenue)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1 font-sans">Bookings</p>
                        <p className="font-serif text-xl text-white">{r.annual_bookings}</p>
                      </div>
                    </div>
                    <TierBadge tier={r.tier} size="sm" />
                  </div>
                );
              })}
            </section>
          )}

          {/* Full ranking */}
          <section className="rounded-lg border border-white/10 overflow-hidden" data-testid="leaderboard-table">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-ink-surface text-[10px] uppercase tracking-widest text-white/50 border-b border-white/10 font-sans">
              <div className="col-span-1">Rank</div>
              <div className="col-span-4">Agent</div>
              <div className="col-span-3 text-right">Annual sales</div>
              <div className="col-span-2 text-right">Bookings</div>
              <div className="col-span-2 text-right">Tier</div>
            </div>
            {rows.map((r) => {
              const isMe = r.agent_id === user?.id;
              const meta = RANK_ICON[r.rank];
              return (
                <div
                  key={r.agent_id}
                  className={`grid grid-cols-12 gap-4 px-5 py-4 text-sm items-center border-b border-white/5 transition-colors ${isMe ? "bg-[#D4AF37]/5" : "bg-ink hover:bg-ink-surface"}`}
                  data-testid={`leaderboard-row-${r.rank}`}
                >
                  <div className="col-span-1">
                    {meta ? (
                      <span className={`inline-flex w-8 h-8 rounded-full border items-center justify-center ${meta.ring}`}>
                        <meta.Icon size={14} className={meta.color} weight="duotone" />
                      </span>
                    ) : (
                      <span className="text-white/40 font-sans">#{r.rank}</span>
                    )}
                  </div>
                  <div className="col-span-4 min-w-0">
                    <p className="text-white font-sans truncate">
                      {r.name}
                      {isMe && <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] ml-2">You</span>}
                    </p>
                    <p className="text-white/40 text-xs font-sans truncate">{r.email}</p>
                  </div>
                  <div className="col-span-3 text-right font-serif text-[#D4AF37]">{formatMoney(r.annual_revenue)}</div>
                  <div className="col-span-2 text-right text-white/80 font-sans">{r.annual_bookings}</div>
                  <div className="col-span-2 text-right">
                    <TierBadge tier={r.tier} size="sm" />
                  </div>
                </div>
              );
            })}
          </section>
        </>
      )}
    </AgentLayout>
  );
}
